import { useEffect, useState } from "react";
import { UserPlus, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/appApi";
import { ROLE_LABELS } from "@/lib/adminConst";
import { Button } from "@/components/ui/button";

const ROLES = ["contributor", "operator", "editor", "publisher", "super_admin", "pending"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ email: "", name: "", role: "contributor", units: [] });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => toast.error("Akses ditolak"));
  useEffect(() => { load(); api.get("/public/units").then((r) => setUnits(r.data)); }, []);

  const create = async () => {
    if (!draft.email) { toast.error("Email wajib"); return; }
    try { await api.post("/users", draft); toast.success("Pengguna dibuat"); setCreating(false); setDraft({ email: "", name: "", role: "contributor", units: [] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const update = async (id, patch) => { try { await api.put(`/users/${id}`, patch); toast.success("Diperbarui"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };

  const toggleUnit = (u, slug) => {
    const units = u.units.includes(slug) ? u.units.filter((x) => x !== slug) : [...u.units, slug];
    update(u.user_id, { units });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-extrabold text-dlh-green">Pengguna</h1>
        <Button onClick={() => setCreating((v) => !v)} className="bg-dlh-green hover:bg-dlh-greenDark"><UserPlus className="h-4 w-4 mr-1" /> Tambah</Button>
      </div>

      <p className="text-sm text-gray-500 mb-4 flex items-start gap-2"><Shield className="h-4 w-4 mt-0.5 shrink-0" /> Akun ditetapkan melalui email Google. Pengguna baru masuk sebagai "Menunggu Penetapan Peran" hingga diberi peran di sini.</p>

      {creating && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 grid sm:grid-cols-2 gap-3">
          <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Email Google" className="border rounded-lg px-3 py-2" data-testid="user-email" />
          <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nama" className="border rounded-lg px-3 py-2" data-testid="user-name" />
          <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="border rounded-lg px-3 py-2" data-testid="user-role">
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <div className="sm:col-span-2 flex flex-wrap gap-1.5">
            {units.map((u) => (
              <button key={u.slug} onClick={() => setDraft({ ...draft, units: draft.units.includes(u.slug) ? draft.units.filter((x) => x !== u.slug) : [...draft.units, u.slug] })}
                className={`text-xs px-2.5 py-1 rounded-full border ${draft.units.includes(u.slug) ? "bg-dlh-green text-white border-dlh-green" : "bg-white border-gray-300"}`}>{u.short_name}</button>
            ))}
          </div>
          <Button onClick={create} data-testid="user-create-btn" className="bg-dlh-green hover:bg-dlh-greenDark sm:col-span-2">Simpan Pengguna</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {users.map((u) => (
          <div key={u.user_id} className="p-4" data-testid={`user-row-${u.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-dlh-green">{u.name} {!u.active && <span className="text-xs text-red-500">(nonaktif)</span>}</p>
                <p className="text-xs text-gray-500">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={u.role} onChange={(e) => update(u.user_id, { role: e.target.value })} className="border rounded-lg px-2 py-1.5 text-sm" data-testid={`role-select-${u.email}`}>
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
                <button onClick={() => update(u.user_id, { active: !u.active })} className={`text-xs px-3 py-1.5 rounded-lg border ${u.active ? "border-red-300 text-red-600" : "border-green-300 text-green-600"}`}>{u.active ? "Nonaktifkan" : "Aktifkan"}</button>
              </div>
            </div>
            {["editor", "contributor"].includes(u.role) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="text-xs text-gray-400 mr-1">Unit ditugaskan:</span>
                {units.map((un) => (
                  <button key={un.slug} onClick={() => toggleUnit(u, un.slug)} className={`text-xs px-2 py-0.5 rounded-full border ${u.units.includes(un.slug) ? "bg-dlh-greenPale text-dlh-green border-dlh-green" : "bg-white border-gray-200 text-gray-500"}`}>{un.short_name}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
